import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";

/**
 * ConfirmDialog — themed replacement for window.confirm().
 *
 * Usage:
 *   const [confirmOpen, setConfirmOpen] = useState(false);
 *   ...
 *   <ConfirmDialog
 *     open={confirmOpen}
 *     title="Delete this lead?"
 *     description="This cannot be undone."
 *     confirmLabel="Delete"
 *     destructive
 *     onCancel={() => setConfirmOpen(false)}
 *     onConfirm={async () => { await doDelete(); setConfirmOpen(false); }}
 *   />
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
      else if (e.key === "Enter") onConfirm?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  const node = (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
      data-testid="confirm-dialog"
    >
      <div
        className="w-full max-w-sm rounded-2xl"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center mb-4"
            style={{
              background: destructive ? "rgba(239, 68, 68, 0.12)" : "var(--lime-glow)",
              color: destructive ? "#f87171" : "var(--lime)",
            }}
          >
            <AlertTriangle size={18} />
          </div>
          <h3 className="text-lg font-semibold mb-2" data-testid="confirm-dialog-title">
            {title}
          </h3>
          {description && (
            <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="confirm-dialog-description">
              {description}
            </p>
          )}
        </div>
        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn-ghost text-sm"
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="text-sm font-semibold px-5 py-2.5 rounded-xl transition"
            style={
              destructive
                ? { background: "#dc2626", color: "#fff", border: "none" }
                : { background: "var(--lime)", color: "#0c0c0c", border: "none" }
            }
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
